import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageSquare, Send, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityLogsSection = ({ leadId }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);

  // Check if user is near bottom of logs container
  const isNearBottom = () => {
    if (!logsContainerRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100; // Within 100px of bottom
  };

  useEffect(() => {
    // Initial fetch with retry logic
    const initialFetch = async () => {
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        try {
          await fetchLogs();
          break; // Success, exit retry loop
        } catch (error) {
          retries++;
          if (retries < maxRetries) {
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retries));
          }
        }
      }
    };
    
    initialFetch();
    
    // Refresh logs every 10 seconds
    const interval = setInterval(fetchLogs, 10000);
    return () => clearInterval(interval);
  }, [leadId]);

  useEffect(() => {
    // Only auto-scroll if user was near bottom or explicitly requested
    if (shouldAutoScroll && logs.length > 0) {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShouldAutoScroll(false); // Reset flag
    }
  }, [logs, shouldAutoScroll]);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/activity-logs`, {
        withCredentials: true
      });
      setLogs(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      // Only show error toast if it's not an authentication error and component is still mounted
      if (loading && error.response?.status !== 401) {
        toast.error("Failed to fetch activity logs");
      }
      setLoading(false);
    }
  };

  const postLog = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setPosting(true);
    try {
      await axios.post(`${API}/leads/${leadId}/activity-logs`, {
        lead_id: leadId,
        message: message.trim()
      }, {
        withCredentials: true
      });
      setMessage("");
      setShouldAutoScroll(true); // Auto-scroll when user posts a message
      fetchLogs();
    } catch (error) {
      console.error("Error posting log:", error);
      toast.error("Failed to post message");
    } finally {
      setPosting(false);
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm("Delete this message?")) return;

    try {
      await axios.delete(`${API}/activity-logs/${logId}`, {
        withCredentials: true
      });
      toast.success("Message deleted");
      fetchLogs();
    } catch (error) {
      console.error("Error deleting log:", error);
      toast.error(error.response?.data?.detail || "Failed to delete message");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <span>Internal Chat</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-4">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5" />
          <span>Internal Chat</span>
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">Team collaboration and notes about this lead</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Messages Display */}
        <div ref={logsContainerRef} className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {logs.length === 0 ? (
            <p className="text-slate-500 text-center py-8 text-sm">
              No notes yet. Start the conversation!
            </p>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="flex space-x-3 group">
                <Avatar className="w-8 h-8 mt-1">
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs">
                    {getInitials(log.user_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-900">{log.user_name}</span>
                      <span className="text-xs text-slate-500">{formatTime(log.created_at)}</span>
                    </div>
                    {(log.user_id === user?.id || user?.role === 'admin') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 px-2"
                        onClick={() => deleteLog(log.id)}
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2">
                    {log.message}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>

        {/* Input Form */}
        <form onSubmit={postLog} className="flex space-x-2 pt-2 border-t">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your note..."
            className="resize-none"
            rows={2}
            disabled={posting}
          />
          <Button
            type="submit"
            disabled={posting || !message.trim()}
            className="shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ActivityLogsSection;
